/**
 * Auth service - orchestrates Cognito, repositories, and authStore.
 * Replaces AuthController for migrated features. Used by initApi, OpenApiConfig, and auth hooks.
 */
import { default as config } from "common/config/config";
import { CognitoService } from "commonlibs/shared-services/auth/cognito/CognitoService";
import RepositoryFactory from "domain/adapter/RepositoryFactory";
import { useAuthStore } from "../store/authStore";

const cognitoService = new CognitoService();

export const authService = {
    getAccessToken: async (): Promise<string | undefined> => { // skipcq: JS-0116
        return cognitoService.getToken();
    },

    getSessionTokens: () => cognitoService.getSessionTokens(),

    loadAuthToken: async (): Promise<void> => {
        try {
            const token = await authService.getAccessToken();
            if (token) {
                useAuthStore.getState().setLoggedIn(true);
            }
        } catch {
            await authService.removeLocalData();
        }
    },

    logout: async (): Promise<void> => {
        await authService.removeLocalData();
        useAuthStore.getState().logout();
        cognitoService.signOut();
    },

    verifyInactivityTime: async (): Promise<void> => {
        const settingsRepo = RepositoryFactory.settingsRepository;
        const lastActivityDate = await settingsRepo.getLastActivityDate();

        if (lastActivityDate) {
            const dateDifference = Date.now() - lastActivityDate.getTime();
            if (dateDifference >= config.inactivityTimeout) {
                await authService.logout();
                return;
            }
        }

        await settingsRepo.setLastActivityDate();
    },

    removeLocalData: async (): Promise<void> => {
        await RepositoryFactory.settingsRepository.removeSettings();
        await RepositoryFactory.authTokenRepository.removeToken();
    },

    signIn: (requestBody: { identifier: number; password: string }) => cognitoService.signIn(requestBody),
};
